/**
 * PDF-Ausgaben des Cockpits: Gesamtreport, Monatsauswertung, Gantt.
 *
 * jspdf und jspdf-autotable werden wie bisher erst beim Klick geladen — sie
 * sind zusammen groesser als der halbe Anwendungscode.
 *
 * Alle Zahlen kommen aus dem Modell. Die frueher hier gerechneten Summen
 * (u. a. die tote `filteredBilledDetails.reduce`-Zeile) sind entfallen.
 */
import type { CellHookData } from 'jspdf-autotable';
import type { CockpitModel, ForecastResult, ProjectRow } from '../../services/cockpitTypes';
import { CAP_HOURS, MO, MO_FULL, documentTypeLabel, euroPlain, statusChip } from './cockpitUi';

export interface PdfContext {
    companyName: string;
    /** „Gesamtes Team" oder der Name der gefilterten Person. */
    filterLabel: string;
    /** „Juli 2026" / „Q3 2026" / „2026". */
    periodText: string;
    periodSlug: string;
    /**
     * Betraege verbergen — derselbe Schalter wie am Bildschirm. Ein Ausdruck,
     * der die verborgenen Zahlen trotzdem zeigt, waere genau der Weg, auf dem
     * Preise doch noch in fremde Haende geraten.
     */
    hideAmounts: boolean;
}

type TableDoc = { lastAutoTable?: { finalY: number } };
const finalY = (doc: unknown, fallback: number): number =>
    (doc as TableDoc).lastAutoTable?.finalY ?? fallback;

const INK: [number, number, number] = [15, 23, 42];
const ZEBRA: [number, number, number] = [248, 250, 252];

const load = async () => {
    const { default: jsPDF } = await import('jspdf');
    const autoTable = (await import('jspdf-autotable')).default;
    return { jsPDF, autoTable };
};

const dateToday = () => new Date().toLocaleDateString('de-DE');

const pct = (part: number, whole: number): string =>
    whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—';

const HIDDEN = '•••';

/** Die eine Geldausgabe des Ausdrucks, `hideAmounts` eingerechnet. */
const moneyOf = (ctx: PdfContext) => (cents: number): string =>
    (ctx.hideAmounts ? HIDDEN : euroPlain(cents));

/** Sammelreport ueber den gewaehlten Zeitraum. */
export const exportCockpitPdf = async (model: CockpitModel, ctx: PdfContext): Promise<void> => {
    const { jsPDF, autoTable } = await load();
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const w = doc.internal.pageSize.getWidth();
    const h = doc.internal.pageSize.getHeight();
    const mg = 15;
    const stamp = dateToday();
    const money = moneyOf(ctx);

    const header = (title: string) => {
        doc.setFillColor(INK[0], INK[1], INK[2]);
        doc.rect(0, 0, w, 18, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(title, mg, 12);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.text(`${ctx.companyName} | ${stamp} | ${ctx.filterLabel} | ${ctx.periodText}`, w - mg, 12, { align: 'right' });
        doc.setTextColor(0, 0, 0);
    };
    const footer = (page: number) => {
        doc.setFontSize(6);
        doc.setTextColor(150);
        doc.text(`Seite ${page} | ${ctx.companyName} | ${stamp}`, w / 2, h - 4, { align: 'center' });
        doc.setTextColor(0);
    };

    // ── Seite 1: Kacheln + Monatsverlauf ──────────────────────────────────
    header('KPI-Cockpit — Kennzahlen');
    footer(1);
    const t = model.tiles;
    const kpis: { label: string; value: string }[] = [
        { label: `Erbracht ${ctx.periodText}`, value: money(t.performedCents) },
        { label: `Abgerechnet ${ctx.periodText}`, value: money(t.billedCents) },
        { label: 'Noch abzurechnen', value: money(t.unbilledCents) },
        { label: 'Offene Forderungen', value: money(t.receivablesOpenCents) },
        { label: `Forecast ${ctx.periodText}`, value: money(t.forecastCents) },
        { label: 'Realisierung', value: t.realisationPct === null ? '—' : `${Math.round(t.realisationPct)}%` },
        { label: 'Auftragsbestand', value: money(t.backlogCents) },
        { label: 'Pipeline (in Planung)', value: money(t.pipelineCents) },
    ];
    const kw = (w - 2 * mg - 12) / 4;
    kpis.forEach((k, i) => {
        const x = mg + (i % 4) * (kw + 4);
        const yy = 26 + Math.floor(i / 4) * 16;
        doc.setFillColor(ZEBRA[0], ZEBRA[1], ZEBRA[2]);
        doc.roundedRect(x, yy, kw, 13, 2, 2, 'F');
        doc.setFontSize(6);
        doc.setTextColor(100);
        doc.text(k.label.toUpperCase(), x + 3, yy + 4);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(INK[0], INK[1], INK[2]);
        doc.text(k.value, x + 3, yy + 11);
        doc.setFont('helvetica', 'normal');
    });
    doc.setTextColor(0);
    doc.setFontSize(7);
    doc.text(
        `Überfällig: ${money(t.receivablesOverdueCents)} (${t.receivablesOverdueCount} Belege) · Begehungen: ${t.inspections}`,
        mg, 26 + 32 + 6,
    );

    let y = 26 + 32 + 12;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(INK[0], INK[1], INK[2]);
    doc.text('Monatsverlauf', mg, y);
    y += 3;
    autoTable(doc, {
        startY: y,
        margin: { left: mg, right: mg },
        head: [['Monat', 'Forecast', 'Erbracht', 'Abgerechnet', 'Realisierung', 'Begehungen']],
        body: model.months.map((m, i) => [
            MO[i], money(m.forecastCents), money(m.performedCents),
            money(m.billedCents), pct(m.performedCents, m.forecastCents), String(m.inspections),
        ]),
        styles: { fontSize: 7, cellPadding: 1.5 },
        headStyles: { fillColor: INK, fontStyle: 'bold', fontSize: 7 },
        alternateRowStyles: { fillColor: ZEBRA },
        columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' } },
    });

    // ── Seite 2: Team + Auslastung ────────────────────────────────────────
    doc.addPage('a4', 'landscape');
    header('Team');
    footer(2);
    autoTable(doc, {
        startY: 26,
        margin: { left: mg, right: mg },
        head: [['Mitarbeiter', 'Projekte', 'Erbracht', 'davon selbst', 'Forecast', 'Realisierung', 'Begehungen', 'Ø/Begehung']],
        body: model.employees.map((e) => [
            e.name, String(e.projectCount), money(e.performedCents), money(e.performedSelfCents),
            money(e.forecastCents), pct(e.performedCents, e.forecastCents), String(e.inspections),
            e.avgPerInspectionCents === null ? '—' : money(e.avgPerInspectionCents),
        ]),
        styles: { fontSize: 6.5, cellPadding: 1.5 },
        headStyles: { fillColor: INK, fontStyle: 'bold', fontSize: 6.5 },
        alternateRowStyles: { fillColor: ZEBRA },
        columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' } },
    });

    const wy = finalY(doc, 80) + 8;
    if (wy < h - 50) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(`Auslastungsprognose (Stunden/Monat, Kapazität ${CAP_HOURS} h)`, mg, wy);
        autoTable(doc, {
            startY: wy + 3,
            margin: { left: mg, right: mg },
            head: [['Mitarbeiter', ...MO, 'Ø']],
            body: model.employees.map((e) => {
                const total = e.workloadMinutes.reduce((s, v) => s + v, 0);
                return [
                    e.name,
                    ...e.workloadMinutes.map((m) => (m > 0 ? `${Math.round(m / 60)}h` : '—')),
                    `${Math.round(total / 12 / 60)}h`,
                ];
            }),
            styles: { fontSize: 6, cellPadding: 1.2, halign: 'center' },
            headStyles: { fillColor: INK, fontSize: 6 },
            alternateRowStyles: { fillColor: ZEBRA },
            columnStyles: { 0: { halign: 'left', fontStyle: 'bold' }, 13: { fillColor: [241, 245, 249], fontStyle: 'bold' } },
            didParseCell: (data: CellHookData) => {
                if (data.section !== 'body' || data.column.index < 1 || data.column.index > 12) return;
                const hours = parseInt(String(data.cell.raw ?? '0'), 10) || 0;
                const p = Math.round((hours / CAP_HOURS) * 100);
                if (p > 100) { data.cell.styles.fillColor = [239, 68, 68]; data.cell.styles.textColor = [255, 255, 255]; }
                else if (p > 85) { data.cell.styles.fillColor = [251, 146, 60]; data.cell.styles.textColor = [255, 255, 255]; }
                else if (p > 60) { data.cell.styles.fillColor = [252, 211, 77]; }
            },
        });
    }

    // ── Seite 3: Projekte + Auftraggeber ──────────────────────────────────
    doc.addPage('a4', 'landscape');
    header('Projekte');
    footer(3);
    autoTable(doc, {
        startY: 26,
        margin: { left: mg, right: mg },
        head: [['Projekt', 'Status', 'Verantwortlich', 'Auftraggeber', 'Vertrag', 'Erbracht', 'Abgerechnet', 'Noch abzur.', 'Auftragsbest.', 'Rest']],
        body: [...model.projects]
            .sort((a, b) => b.contractCents - a.contractCents)
            .map((p) => [
                p.name.substring(0, 34), statusChip(p.status).label, p.responsibleName,
                p.customerName.substring(0, 24), money(p.contractCents), money(p.performedCents),
                money(p.billedCents), money(p.unbilledCents), money(p.backlogCents),
                p.daysRemaining === null ? '—' : `${p.daysRemaining}d`,
            ]),
        styles: { fontSize: 6, cellPadding: 1.5 },
        headStyles: { fillColor: INK, fontSize: 6 },
        alternateRowStyles: { fillColor: ZEBRA },
        columnStyles: { 0: { cellWidth: 46 }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' }, 8: { halign: 'right' }, 9: { halign: 'right' } },
    });

    const cy = finalY(doc, 120) + 8;
    if (cy < h - 40) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(INK[0], INK[1], INK[2]);
        doc.text('Top Auftraggeber', mg, cy);
        autoTable(doc, {
            startY: cy + 3,
            margin: { left: mg, right: mg },
            head: [['Auftraggeber', 'Projekte', 'Erbracht', 'Abgerechnet']],
            body: model.customers.slice(0, 10).map((c) => [
                c.name, String(c.projectCount), money(c.performedCents), money(c.billedCents),
            ]),
            styles: { fontSize: 7, cellPadding: 2 },
            headStyles: { fillColor: INK, fontSize: 7 },
            alternateRowStyles: { fillColor: ZEBRA },
            columnStyles: { 1: { halign: 'center' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
        });
    }

    doc.save(`GefAS_Cockpit_${ctx.periodSlug}.pdf`);
};

/** Ein Monat im Detail — die Belege und Leistungen hinter den Balken. */
export const exportMonthPdf = async (args: {
    model: CockpitModel;
    forecast: ForecastResult;
    monthIndex: number;
    ctx: PdfContext;
    projectFilter: string;
    employeeFilter: string;
}): Promise<void> => {
    const { model, forecast, monthIndex, ctx } = args;
    const { jsPDF, autoTable } = await load();
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const w = doc.internal.pageSize.getWidth();
    const h = doc.internal.pageSize.getHeight();
    const mg = 15;
    const monthName = MO_FULL[monthIndex];
    const year = model.period.year;
    const money = moneyOf(ctx);

    const performed = model.performedDetails.filter((d) =>
        d.month === monthIndex
        && (args.projectFilter === 'all' || d.projectId === args.projectFilter)
        && (args.employeeFilter === 'all' || d.performerId === args.employeeFilter));
    const billed = model.billedDetails.filter((d) =>
        d.month === monthIndex && (args.projectFilter === 'all' || d.projectId === args.projectFilter));

    const performedSum = performed.reduce((a, d) => a + d.cents, 0);
    const billedSum = billed.reduce((a, d) => a + d.cents, 0);
    const forecastSum = model.months[monthIndex]?.forecastCents ?? 0;

    doc.setFillColor(INK[0], INK[1], INK[2]);
    doc.rect(0, 0, w, 22, 'F');
    doc.setTextColor(255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`Monatsauswertung: ${monthName} ${year}`, mg, 14);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(`${ctx.companyName} | ${dateToday()} | ${ctx.filterLabel}`, w - mg, 14, { align: 'right' });
    doc.setTextColor(0);

    let y = 30;
    const kpiW = (w - 2 * mg - 8) / 3;
    ([
        ['Forecast', money(forecastSum)],
        ['Erbracht', money(performedSum)],
        ['Abgerechnet', money(billedSum)],
    ] as [string, string][]).forEach(([label, value], i) => {
        const x = mg + i * (kpiW + 4);
        doc.setFillColor(ZEBRA[0], ZEBRA[1], ZEBRA[2]);
        doc.roundedRect(x, y, kpiW, 16, 3, 3, 'F');
        doc.setTextColor(100);
        doc.setFontSize(7);
        doc.text(label.toUpperCase(), x + 4, y + 6);
        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(INK[0], INK[1], INK[2]);
        doc.text(value, x + 4, y + 13);
        doc.setFont('helvetica', 'normal');
    });
    doc.setTextColor(0);
    y += 24;

    const diff = performedSum - forecastSum;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(
        `Abweichung Erbracht gegen Forecast: ${diff >= 0 ? '+' : ''}${money(diff)} (${pct(performedSum, forecastSum)})`,
        mg, y,
    );
    doc.setFont('helvetica', 'normal');
    y += 6;

    if (performed.length > 0) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text(`Erbrachte Leistungen — ${monthName}`, mg, y);
        autoTable(doc, {
            startY: y + 2,
            margin: { left: mg, right: mg },
            head: [['Datum', 'Projekt', 'Leistung', 'Menge', 'Einheit', 'Betrag', 'Ausführend']],
            body: [...performed]
                .sort((a, b) => a.date.localeCompare(b.date))
                .map((d) => [
                    d.date.slice(8, 10) + '.' + d.date.slice(5, 7) + '.',
                    d.projectName.substring(0, 26),
                    (d.note ? `${d.serviceTitle} (${d.note})` : d.serviceTitle).substring(0, 26),
                    String(d.quantity), d.unit ?? '', money(d.cents), d.performerName.substring(0, 18),
                ]),
            foot: [['', '', '', '', 'Σ Erbracht', money(performedSum), '']],
            styles: { fontSize: 6.5, cellPadding: 1.5 },
            headStyles: { fillColor: [16, 185, 129], fontStyle: 'bold', fontSize: 6.5 },
            footStyles: { fillColor: [209, 250, 229], fontStyle: 'bold', textColor: [6, 95, 70] },
            alternateRowStyles: { fillColor: ZEBRA },
            columnStyles: { 0: { cellWidth: 14 }, 3: { halign: 'right', cellWidth: 13 }, 5: { halign: 'right', cellWidth: 20 } },
        });
        y = finalY(doc, y + 20) + 8;
    }

    if (billed.length > 0) {
        if (y > h - 60) { doc.addPage(); y = 20; }
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0);
        doc.text(`Belege — ${monthName}`, mg, y);
        autoTable(doc, {
            startY: y + 2,
            margin: { left: mg, right: mg },
            head: [['Datum', 'Beleg', 'Art', 'Projekt', 'Auftraggeber', 'Netto']],
            body: [...billed]
                .sort((a, b) => a.date.localeCompare(b.date))
                .map((d) => [
                    d.date.slice(8, 10) + '.' + d.date.slice(5, 7) + '.',
                    d.invoiceNumber, documentTypeLabel(d.documentType), d.projectName.substring(0, 24),
                    d.customerName.substring(0, 24), money(d.cents),
                ]),
            foot: [['', '', '', '', 'Σ Abgerechnet', money(billedSum)]],
            styles: { fontSize: 6.5, cellPadding: 1.5 },
            headStyles: { fillColor: [139, 92, 246], fontStyle: 'bold', fontSize: 6.5 },
            footStyles: { fillColor: [237, 233, 254], fontStyle: 'bold', textColor: [91, 33, 182] },
            alternateRowStyles: { fillColor: ZEBRA },
            columnStyles: { 0: { cellWidth: 14 }, 5: { halign: 'right', cellWidth: 22 } },
        });
        y = finalY(doc, y + 20) + 8;
    }

    // Abweichung je Projekt: Forecast aus der Prognose, Erbracht aus dem Modell.
    const names = new Map(model.projects.map((p) => [p.id, p.name]));
    const rows = new Map<string, { name: string; forecast: number; performed: number }>();
    Object.entries(forecast.byProject).forEach(([pid, series]) => {
        const value = series[monthIndex] ?? 0;
        if (value === 0) return;
        // Dieselbe Schranke wie im Dialog (MonthDrilldown): die Prognose kennt
        // ALLE Projekte, das Modell nur die gefilterten. Ohne sie stuenden unter
        // einem Mitarbeiterfilter fremde Projekte als nackte UUID im Ausdruck
        // und die Summe widerspraeche der Kachel.
        if (!names.has(pid)) return;
        if (args.projectFilter !== 'all' && pid !== args.projectFilter) return;
        rows.set(pid, { name: names.get(pid) ?? pid, forecast: value, performed: 0 });
    });
    performed.forEach((d) => {
        const row = rows.get(d.projectId) ?? { name: d.projectName, forecast: 0, performed: 0 };
        row.performed += d.cents;
        rows.set(d.projectId, row);
    });
    if (rows.size > 0) {
        if (y > h - 50) { doc.addPage(); y = 20; }
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0);
        doc.text(`Abweichung nach Projekt — ${monthName}`, mg, y);
        autoTable(doc, {
            startY: y + 2,
            margin: { left: mg, right: mg },
            head: [['Projekt', 'Forecast', 'Erbracht', 'Abweichung']],
            body: [...rows.values()]
                .sort((a, b) => Math.abs(b.performed - b.forecast) - Math.abs(a.performed - a.forecast))
                .map((r) => [
                    r.name.substring(0, 38), money(r.forecast), money(r.performed),
                    `${r.performed - r.forecast >= 0 ? '+' : ''}${money(r.performed - r.forecast)}`,
                ]),
            styles: { fontSize: 7, cellPadding: 2 },
            headStyles: { fillColor: [51, 65, 85], fontStyle: 'bold', fontSize: 7 },
            alternateRowStyles: { fillColor: ZEBRA },
            columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' } },
        });
    }

    const pages = doc.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
        doc.setPage(i);
        doc.setFontSize(6);
        doc.setTextColor(150);
        doc.text(`Seite ${i}/${pages} | ${ctx.companyName} | ${monthName} ${year}`, w / 2, h - 4, { align: 'center' });
    }

    doc.save(`GefAS_Monatsauswertung_${year}-${String(monthIndex + 1).padStart(2, '0')}.pdf`);
};

/** Projektbalken des Jahres — Tabelle plus Zeitstrahl, wie bisher. */
export const exportGanttPdf = async (args: {
    rows: ProjectRow[];
    numbers: Record<string, string>;
    year: number;
    sortLabel: string;
    ctx: PdfContext;
}): Promise<void> => {
    const { rows, numbers, year, sortLabel, ctx } = args;
    const { jsPDF, autoTable } = await load();
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const w = doc.internal.pageSize.getWidth();
    const h = doc.internal.pageSize.getHeight();
    const mg = 10;
    const money = moneyOf(ctx);

    doc.setFillColor(INK[0], INK[1], INK[2]);
    doc.rect(0, 0, w, 16, 'F');
    doc.setTextColor(255);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`Projekt-Gantt ${year} — Sortierung: ${sortLabel}`, mg, 11);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(`${ctx.companyName} | ${dateToday()} | ${ctx.filterLabel}`, w - mg, 11, { align: 'right' });
    doc.setTextColor(0);

    const days = (from: string | null, to: string | null): number | null => {
        if (!from || !to) return null;
        return Math.ceil((Date.parse(to) - Date.parse(from)) / 864e5);
    };
    const de = (iso: string | null) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '—');

    autoTable(doc, {
        startY: 22,
        margin: { left: mg, right: mg },
        head: [['#', 'Projekt', 'Verantwortlich', 'Status', 'Start', 'Ende', 'Laufzeit', 'Rest', 'Fortschritt', 'Vertrag']],
        body: rows.map((p) => {
            const duration = days(p.startDate, p.endDate);
            const progress = p.contractCents > 0 ? Math.round((p.performedLifetimeCents / p.contractCents) * 100) : 0;
            return [
                numbers[p.id] ?? '—', p.name.substring(0, 30), p.responsibleName, statusChip(p.status).label,
                de(p.startDate), de(p.endDate), duration === null ? '—' : `${duration}d`,
                p.daysRemaining === null ? '—' : `${p.daysRemaining}d`, `${progress}%`, money(p.contractCents),
            ];
        }),
        styles: { fontSize: 6, cellPadding: 1.5 },
        headStyles: { fillColor: INK, fontStyle: 'bold', fontSize: 6 },
        alternateRowStyles: { fillColor: ZEBRA },
        columnStyles: {
            0: { cellWidth: 9 }, 1: { cellWidth: 36 }, 2: { cellWidth: 24 }, 3: { cellWidth: 12 },
            4: { cellWidth: 16 }, 5: { cellWidth: 16 }, 6: { cellWidth: 14, halign: 'right' },
            7: { cellWidth: 12, halign: 'right' }, 8: { cellWidth: 14, halign: 'right' }, 9: { cellWidth: 18, halign: 'right' },
        },
        didParseCell: (data: CellHookData) => {
            if (data.section !== 'body' || data.column.index !== 7) return;
            const v = parseInt(String(data.cell.raw ?? '999'), 10);
            if (v <= 30 && v > 0) { data.cell.styles.textColor = [239, 68, 68]; data.cell.styles.fontStyle = 'bold'; }
            else if (v <= 60) { data.cell.styles.textColor = [245, 158, 11]; }
        },
    });

    const after = finalY(doc, 100);
    if (after < h - 35) {
        const areaW = w - 2 * mg;
        const barY = after + 6;
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.text('Zeitstrahl', mg, barY);
        const bsY = barY + 3;
        MO.forEach((m, i) => {
            const x = mg + i * (areaW / 12);
            const tone = i % 2 === 0 ? 248 : 241;
            doc.setFillColor(tone, tone + 2, tone + 4);
            doc.rect(x, bsY, areaW / 12, 5, 'F');
            doc.setFontSize(5);
            doc.setTextColor(100);
            doc.text(m, x + areaW / 24, bsY + 3.5, { align: 'center' });
        });
        const yearStart = Date.parse(`${year}-01-01`);
        const yearSpan = Date.parse(`${year}-12-31`) - yearStart;
        const todayFrac = Math.min(1, Math.max(0, (Date.now() - yearStart) / yearSpan));
        doc.setDrawColor(239, 68, 68);
        doc.setLineWidth(0.3);
        doc.line(mg + todayFrac * areaW, bsY, mg + todayFrac * areaW, bsY + 5 + rows.length * 3);
        rows.forEach((p, i) => {
            if (!p.startDate) return;
            const s = Math.min(1, Math.max(0, (Date.parse(p.startDate) - yearStart) / yearSpan));
            const e = p.endDate ? Math.min(1, Math.max(0, (Date.parse(p.endDate) - yearStart) / yearSpan)) : 1;
            const bx = mg + s * areaW;
            const bw = Math.max(2, (e - s) * areaW);
            const by = bsY + 7 + i * 3;
            const color: [number, number, number] = p.status === 'active' ? [16, 185, 129]
                : p.status === 'planning' ? [96, 165, 250] : [245, 158, 11];
            doc.setFillColor(color[0], color[1], color[2]);
            doc.roundedRect(bx, by, bw, 2, 0.5, 0.5, 'F');
            doc.setFontSize(3.5);
            doc.setTextColor(80);
            doc.setFont('helvetica', 'normal');
            doc.text(p.name.substring(0, 22), bx + bw + 1, by + 1.5);
        });
    }
    doc.setFontSize(6);
    doc.setTextColor(150);
    doc.text(`${ctx.companyName} | ${dateToday()}`, w / 2, h - 4, { align: 'center' });
    doc.save(`GefAS_Gantt_${year}.pdf`);
};
